import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { GenericTable } from 'components/table.component';
import { ColumnConfig } from 'interfaces/table.interface';
import { NotArchivedEngagement } from 'interfaces/types.interface';
import { EngagementService } from 'services/engagements.service';
import { RootState, UUID, dayjs } from '@umanforge/common';
import { EventManager } from 'interfaces/eventManager.interface';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';
import CompletionIndicator from 'components/wrapperEventManager/completionIndicator.component';

interface NotArchivedEngagementsTableProps {
  eventManager: EventManager;
  chartSectionId: UUID;
}

const NotArchivedEngagementsTable: React.FC<NotArchivedEngagementsTableProps> = ( { chartSectionId, eventManager } ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const columns: ColumnConfig<NotArchivedEngagement>[] = [
    {
      key: 'eventTitle',
      label: t( 'common:title' ),
      type: 'string',
    },
    {
      key: 'participants',
      label: t( 'common:participants' ),
      type: 'string',
      getValue: row => {
        return row.participants
          .map( p => {
            const fullname = p.fullname === DEFAULT_BACKLOG.NAME ? ( eventManager.backlogName === DEFAULT_BACKLOG.NAME ? t( DEFAULT_BACKLOG.NAME ) : eventManager.backlogName ) : p.fullname;
            return fullname;
          } )
          .join( ', ' );
      }
    },
    {
      key: 'themeName',
      label: t( 'common:theme' ),
      type: 'theme',
      getValue: row => {
        return {
          name: row.themeName ?? '',
          color: row.color ?? undefined,
        };
      }
    },
    {
      key: 'realDeliveryDate',
      label: t( 'translation:engagement_date' ),
      type: 'date',
      getValue: row => {
        return row.realDeliveryDate ? dayjs( row.realDeliveryDate ).format( 'L' ) : '';
      }
    },
    {
      key: 'status',
      label: t( 'common:status' ),
      type: 'actions',
      getValue: row => {
        const statusKey = row.completed ? 'translation:finished' : 'translation:not_finished';

        return (
          <div className="grid grid-cols-[minmax(0,1.5fr)_auto_auto] items-center gap-x-3">
            <div className="flex items-center text-left">
              <span className="text-sm">
                {t( statusKey )}
              </span>
            </div>
            <div className="flex justify-center">
              {row.numberOfLateDays > 0 ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium min-w-[2.25rem] justify-center">
                  {row.numberOfLateDays}
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium min-w-[2.25rem] justify-center opacity-0">
                  0
                </span>
              )}
            </div>
            <div className="flex justify-end min-w-[3.5rem]">
              {row.complatedPercentage != null ? (
                <CompletionIndicator percentage={row.complatedPercentage ?? undefined} />
              ) : (
                <div className="opacity-0">
                  <CompletionIndicator percentage={0} />
                </div>
              )}
            </div>
          </div>
        );
      }
    },
  ];

  const getData = useCallback( async ( page: number, step: number, sort: { key: string; order: 'asc' | 'desc' } ) => {
    void sort;
    const result = await EngagementService.getNotArchivedEngagementsByChartSection( chartSectionId, page, step, token, dispatch );

    return {
      total: result.total,
      datas: result.list,
    };
  }, [ dispatch, chartSectionId, token ] );

  return (
    <div className="mt-10 mb-10">
      <GenericTable<NotArchivedEngagement>
        getData={ ( page, step, sort ) => { return getData( page, step, sort ); } }
        columns={columns}
        title={t( 'translation:ongoing_engagements' )}
        rowsPerPage={10}
        defaultSortKey="realDeliveryDate"
        defaultSortOrder="desc"
      />
    </div>
  );
};

export { NotArchivedEngagementsTable };
